/**
 * useNovosPedidos — D-183 #15 (2026-05-29): SSE em vez de polling 15s.
 *
 * Comportamento:
 *   - Abre EventSource('/api/delivery/stream') quando o usuário é caixa/admin.
 *   - Cada evento `novo-pedido` dispara CustomEvent('myros-novo-pedido', detail).
 *   - <NotificacoesPedidos> escuta o CustomEvent e renderiza toast + som.
 *   - Mantém `Set<id>` de pedidos já vistos em sessionStorage pra anti-duplicação
 *     entre abas (caso o backend já tenha publicado o pedido e a aba reabre).
 *   - Auto-reconnect nativo do EventSource quando a conexão cai.
 *
 * Ganhos sobre o polling antigo:
 *   - Latência: ≤5s (vs 15s)
 *   - Carga: 1 polling no server vs N (clientes)
 *   - Bateria: menos wake-ups em mobile
 *
 * Fallback: se EventSource não estiver disponível (raro — IE11 não conta), o
 * hook não faz nada. Painel ainda atualiza via refresh manual ou navigation.
 *
 * Ativação: `useNovosPedidos(user?.perfil === 'caixa' || user?.perfil === 'admin')`
 *   Atendente NÃO recebe (não opera delivery).
 */
import { useEffect } from 'react'
import type { DeliveryOrder } from '../api/deliveryClient'

export const STORAGE_KEY = 'myros_seen_orders'
export const ACTIVE_STATUSES = new Set(['paid', 'received', 'preparing'])

/**
 * Lógica pura mantida pra retrocompat com testes do D-96. Mesmo contrato:
 * dados batch + Set seen + flag firstRun → retorna {novos, newSeen}.
 *
 * No fluxo SSE atual essa função NÃO é mais chamada pelo hook, mas continua
 * exportada (testes legados e bootstrap de futuro fallback de polling).
 */
export function detectarNovosPedidos(
  orders: DeliveryOrder[],
  seen: Set<string>,
  firstRun: boolean,
): { novos: DeliveryOrder[]; newSeen: Set<string> } {
  const newSeen = new Set<string>()
  const novos: DeliveryOrder[] = []
  for (const o of orders) {
    const id = String(o.id)
    newSeen.add(id)
    if (!ACTIVE_STATUSES.has(o.status)) continue
    if (!seen.has(id)) novos.push(o)
  }
  return { novos: firstRun ? [] : novos, newSeen }
}

/** Mantido por compat — alguns scripts importam. SSE não usa isso. */
export const POLL_MS = 15_000

export function useNovosPedidos(enabled: boolean) {
  useEffect(() => {
    if (!enabled) return
    if (typeof EventSource === 'undefined') return

    let seen: Set<string>
    try {
      seen = new Set(JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '[]'))
    } catch {
      seen = new Set()
    }

    // EventSource respeita o cookie httpOnly de sessão (D-120) pra autenticar
    // — same-origin, browser anexa cookies automaticamente.
    const es = new EventSource('/api/delivery/stream')

    es.addEventListener('novo-pedido', (ev) => {
      try {
        const o = JSON.parse((ev as MessageEvent).data) as DeliveryOrder
        const id = String(o.id)
        // Dedup: se já vimos esse pedido nesta sessão, ignora (evita duplicar
        // toast quando reabre aba ou após reconnect)
        if (seen.has(id)) return
        if (!ACTIVE_STATUSES.has(o.status)) return
        seen.add(id)
        try {
          sessionStorage.setItem(STORAGE_KEY, JSON.stringify([...seen]))
        } catch { /* storage indisponível */ }
        window.dispatchEvent(new CustomEvent('myros-novo-pedido', { detail: o }))
      } catch (err) {
        console.warn('[useNovosPedidos] parse novo-pedido falhou', err)
      }
    })

    es.addEventListener('connected', () => {
      // Log de debug — pode ser removido se ficar verbose
      console.info('[useNovosPedidos] SSE conectado')
    })

    es.onerror = () => {
      // EventSource reconecta sozinho — logamos só pra visibilidade
      console.warn('[useNovosPedidos] SSE conexão perdida, reconectando...')
    }

    return () => {
      es.close()
    }
  }, [enabled])
}
